/* IndexedDB: outbox, archive, content cache, plan cache (03 §3.2–3.6).
 *
 * The load-bearing guarantee is in `appendEvent`: the write is AWAITED before
 * the UI advances to the next Card (03 §3.7). Killing the app mid-session then
 * loses at most the render, never the answer.
 *
 * Acknowledged events move to a 14-day ARCHIVE rather than being deleted (A16).
 * If the server is ever restored from a snapshot, its `state_epoch` changes and
 * the client re-POSTs that archive — safe by idempotency, since anything the
 * restored server still holds collapses to `duplicate`.
 */

const DB_NAME = 'ascent';
const DB_VERSION = 1;

const STORE_OUTBOX = 'outbox';
const STORE_ARCHIVE = 'archive';
const STORE_UNITS = 'units';
const STORE_META = 'meta';

export interface OutboxEvent {
  event_id: string;
  type: 'review' | 'unit_completion';
  occurred_at: string;
  schema_version: number;
  payload: Record<string, unknown>;
  /** Local-only bookkeeping, stripped before the event is sent. */
  queued_at: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        db.createObjectStore(STORE_OUTBOX, { keyPath: 'event_id' });
      }
      if (!db.objectStoreNames.contains(STORE_ARCHIVE)) {
        const archive = db.createObjectStore(STORE_ARCHIVE, { keyPath: 'event_id' });
        archive.createIndex('queued_at', 'queued_at');
      }
      if (!db.objectStoreNames.contains(STORE_UNITS)) {
        db.createObjectStore(STORE_UNITS, { keyPath: 'unit_id' });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        // Resolve on TRANSACTION completion, not request success: only then is
        // the write actually durable.
        t.oncomplete = () => resolve(req.result);
        t.onerror = () => reject(t.error ?? new Error('indexedDB transaction failed'));
        t.onabort = () => reject(t.error ?? new Error('indexedDB transaction aborted'));
      }),
  );
}

function uuidV4(): string {
  const c: Crypto = crypto;
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Append an immutable event. AWAIT this before advancing the UI.
 *
 * The client mints the id, so a retry after a lost response is deduped by the
 * server rather than double-counted (05 §1.5.2).
 */
export async function appendEvent(
  type: OutboxEvent['type'],
  payload: Record<string, unknown>,
  occurredAt: string = new Date().toISOString(),
): Promise<OutboxEvent> {
  const event: OutboxEvent = {
    event_id: uuidV4(),
    type,
    occurred_at: occurredAt,
    schema_version: 1,
    payload,
    queued_at: new Date().toISOString(),
  };
  await tx(STORE_OUTBOX, 'readwrite', (s) => s.put(event) as unknown as IDBRequest<unknown>);
  return event;
}

export function readOutbox(): Promise<OutboxEvent[]> {
  return tx<OutboxEvent[]>(STORE_OUTBOX, 'readonly', (s) => s.getAll() as IDBRequest<OutboxEvent[]>);
}

export async function outboxCount(): Promise<number> {
  return (await readOutbox()).length;
}

/** Move acknowledged events to the archive; drop anything past the 14-day window. */
export async function acknowledge(eventIds: string[]): Promise<void> {
  if (eventIds.length === 0) return;
  const db = await openDb();
  const cutoff = new Date(Date.now() - 14 * 86_400_000).toISOString();

  await new Promise<void>((resolve, reject) => {
    const t = db.transaction([STORE_OUTBOX, STORE_ARCHIVE], 'readwrite');
    const outbox = t.objectStore(STORE_OUTBOX);
    const archive = t.objectStore(STORE_ARCHIVE);

    for (const id of eventIds) {
      const get = outbox.get(id);
      get.onsuccess = () => {
        const event = get.result as OutboxEvent | undefined;
        if (event) {
          archive.put(event);
          outbox.delete(id);
        }
      };
    }

    // Prune the archive in the same transaction.
    const range = IDBKeyRange.upperBound(cutoff);
    const cursorReq = archive.index('queued_at').openCursor(range);
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/** Everything still worth replaying after a server restore (§3.3). */
export function readArchive(): Promise<OutboxEvent[]> {
  return tx<OutboxEvent[]>(STORE_ARCHIVE, 'readonly', (s) => s.getAll() as IDBRequest<OutboxEvent[]>);
}

// ---- Content + plan cache -------------------------------------------------

export interface CachedUnit {
  unit_id: string;
  content_hash: string;
  body: unknown;
}

export function putUnit(unit: CachedUnit): Promise<unknown> {
  return tx(STORE_UNITS, 'readwrite', (s) => s.put(unit) as unknown as IDBRequest<unknown>);
}

export function getUnit(unitId: string): Promise<CachedUnit | undefined> {
  return tx<CachedUnit | undefined>(STORE_UNITS, 'readonly',
    (s) => s.get(unitId) as IDBRequest<CachedUnit | undefined>);
}

export function setMeta(key: string, value: unknown): Promise<unknown> {
  return tx(STORE_META, 'readwrite', (s) => s.put({ key, value }) as unknown as IDBRequest<unknown>);
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await tx<{ key: string; value: T } | undefined>(STORE_META, 'readonly',
    (s) => s.get(key) as IDBRequest<{ key: string; value: T } | undefined>);
  return row?.value;
}

/** Drop cached Units when the active release changes (03 §3.6). */
export async function clearUnitCache(): Promise<void> {
  await tx(STORE_UNITS, 'readwrite', (s) => s.clear() as unknown as IDBRequest<unknown>);
}
